import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Bell, BellRing, Moon, Printer, Sunrise, Utensils } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { gregorianToBangla, formatBanglaDate } from "@/lib/bengaliDate";
import { BD_CITIES } from "@/data/bdCities";
import {
  formatTime,
  hijriString,
  METHODS,
  type MethodId,
} from "@/lib/calculators/prayerTimes";
import {
  findRamadanStart,
  getHijri,
  isRamadan,
  ramadanMonthDays,
  sehriIftarFor,
} from "@/lib/calculators/sehriIftar";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Sehri & Iftar Time", bn: "সেহরি ও ইফতারের সময়" },
  desc: {
    en: "Sehri end and Iftar times for all 64 districts of Bangladesh, with full Ramadan calendar and a live countdown to Iftar.",
    bn: "বাংলাদেশের ৬৪ জেলার সেহরি ও ইফতারের সময়সূচী, পূর্ণ রমাদান ক্যালেন্ডার ও ইফতারের লাইভ কাউন্টডাউনসহ।",
  },
  city: { en: "City", bn: "শহর" },
  method: { en: "Method", bn: "মেথড" },
  date: { en: "Date", bn: "তারিখ" },
  precaution: { en: "Precaution before Sehri (min)", bn: "সেহরির পূর্বে সতর্কতা (মিনিট)" },
  sehri: { en: "Sehri ends", bn: "সেহরির শেষ সময়" },
  iftar: { en: "Iftar", bn: "ইফতারের সময়" },
  fasting: { en: "Today's fast", bn: "আজকের রোজা" },
  countdown: { en: "Time until Iftar", bn: "ইফতারের বাকি" },
  notRamadan: { en: "Today is not Ramadan", bn: "আজ রমাদান নয়" },
  nextRamadan: { en: "Next Ramadan begins", bn: "আগামী রমাদান শুরু" },
  approxNote: {
    en: "Subject to local moon sighting in Bangladesh.",
    bn: "বাংলাদেশে স্থানীয় চাঁদ দেখার উপর নির্ভর করে।",
  },
  prayerLink: { en: "Open Prayer Times →", bn: "নামাজের সময় দেখুন →" },
  showCalendar: { en: "Full Ramadan calendar", bn: "পূর্ণ রমাদান ক্যালেন্ডার" },
  notify: { en: "Enable Iftar notification", bn: "ইফতারের নোটিফিকেশন চালু" },
  notifyOn: { en: "Notification scheduled", bn: "নোটিফিকেশন সেট করা আছে" },
  print: { en: "Print", bn: "প্রিন্ট" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  { q: { en: "When does Sehri end?", bn: "সেহরি কখন শেষ?" }, a: { en: "At Fajr — the start of the dawn prayer time. Many in Bangladesh stop a few minutes earlier as a precaution.", bn: "ফজরের সময় শুরু হলে। বাংলাদেশে অনেকে কয়েক মিনিট আগে সতর্কতা হিসেবে খাওয়া বন্ধ করেন।" } },
  { q: { en: "When is Iftar?", bn: "ইফতার কখন?" }, a: { en: "At Maghrib — the moment of sunset.", bn: "মাগরিবের সময় — সূর্যাস্তের মুহূর্তে।" } },
  { q: { en: "How many days is Ramadan?", bn: "রমাদান কত দিন?" }, a: { en: "29 or 30 days depending on the lunar moon sighting.", bn: "চাঁদ দেখার ভিত্তিতে ২৯ বা ৩০ দিন।" } },
  { q: { en: "How long is Ramadan fasting in Bangladesh?", bn: "বাংলাদেশে রোজা কত ঘণ্টা?" }, a: { en: "Roughly 13.5–15 hours depending on season — longer in summer Ramadans.", bn: "মৌসুমভেদে প্রায় ১৩.৫–১৫ ঘণ্টা — গ্রীষ্মে বেশি।" } },
  { q: { en: "Is the fast valid without Sehri?", bn: "সেহরি না খেয়ে রোজা?" }, a: { en: "Yes — Sehri is a Sunnah, not a condition. The fast is valid without it but you miss great reward.", bn: "হ্যাঁ — সেহরি সুন্নত, শর্ত নয়। ছাড়লে রোজা শুদ্ধ, কিন্তু সওয়াব কমে।" } },
  { q: { en: "If I eat by mistake, does the fast break?", bn: "ভুলে খেলে রোজা ভাঙবে?" }, a: { en: "No — accidental eating or drinking does not break the fast. Resume immediately when you remember.", bn: "না — ভুলে খেলে বা পান করলে রোজা ভাঙে না। স্মরণ হলে সাথে সাথে বন্ধ করুন।" } },
  { q: { en: "What is the niyyat for fasting?", bn: "রোজার নিয়ত?" }, a: { en: "Intention is in the heart. The traditional Arabic wording is given on this page with Bengali transliteration.", bn: "নিয়ত মনে মনে যথেষ্ট। প্রচলিত আরবি উচ্চারণ ও বাংলা অনুবাদ এ পেইজে দেওয়া আছে।" } },
  { q: { en: "Best calculation method for BD?", bn: "BD-র জন্য কোন মেথড?" }, a: { en: "Karachi (University of Islamic Sciences) — used by Islamic Foundation Bangladesh.", bn: "করাচী মেথড — ইসলামিক ফাউন্ডেশন বাংলাদেশ অনুসরণ করে।" } },
  { q: { en: "Why does Iftar time differ slightly from local mosque?", bn: "মসজিদের ইফতারের সাথে পার্থক্য কেন?" }, a: { en: "Mosques may apply a 1–3 minute precaution buffer or use a different method/coordinates.", bn: "মসজিদ কখনো ১–৩ মিনিট সতর্কতা যোগ করে, বা ভিন্ন মেথড/স্থানাঙ্ক ব্যবহার করে।" } },
  { q: { en: "When is Laylatul Qadr?", bn: "লাইলাতুল কদর কবে?" }, a: { en: "Among the last 10 odd nights of Ramadan — most likely the 27th, but seek it across all odd nights.", bn: "রমাদানের শেষ ১০ রাতের বিজোড় রাতে — সবচেয়ে সম্ভাব্য ২৭তম রাত, তবে সব বিজোড় রাতেই অন্বেষণ করুন।" } },
];

const STORAGE_KEY = "sehri-iftar-prefs-v1";

export const Route = createFileRoute("/sehri-iftar-time")({
  head: () => {
    const seo = buildSeo({
      title: "সেহরি ও ইফতারের সময় ২০২৬ — Sehri Iftar Time Bangladesh",
      description:
        "বাংলাদেশের সেহরি ও ইফতারের সময়সূচি ২০২৬। ৬৪ জেলার রমাদান ক্যালেন্ডার। ইফতারের কাউন্টডাউন। Live Sehri Iftar time for all 64 districts of Bangladesh, Ramadan 2026 calendar.",
      path: "/sehri-iftar-time",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Sehri & Iftar Time", path: "/sehri-iftar-time" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: SehriIftarPage,
});

function SehriIftarPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];

  useEffect(() => {
    if (!useAppStore.getState().langTouched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [cityKey, setCityKey] = useState("dhaka");
  const [method, setMethod] = useState<MethodId>("Karachi");
  const [precaution, setPrecaution] = useState(0);
  const [date, setDate] = useState<Date>(new Date());
  const [now, setNow] = useState<Date>(new Date());
  const [showCal, setShowCal] = useState(true);
  const [notifyOn, setNotifyOn] = useState(false);
  const notifyTimerRef = useRef<number | null>(null);

  // restore
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.cityKey) setCityKey(d.cityKey);
      if (d.method) setMethod(d.method);
      if (typeof d.precaution === "number") setPrecaution(d.precaution);
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ cityKey, method, precaution }));
    } catch { /* noop */ }
  }, [cityKey, method, precaution]);

  // 1-second ticker (countdown is the hero)
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const city = BD_CITIES.find((c) => c.key === cityKey) ?? BD_CITIES[0];

  const today = useMemo(
    () => sehriIftarFor(city.lat, city.lon, date, method, precaution),
    [city.lat, city.lon, date, method, precaution],
  );
  const liveToday = useMemo(
    () => sehriIftarFor(city.lat, city.lon, now, method, precaution),
    [city.lat, city.lon, now, method, precaution],
  );

  const ramadanNow = isRamadan(date);
  const hijri = getHijri(date);

  const fmt = (d: Date) => {
    const s = formatTime(d, "12");
    return lang === "bn" ? toBengaliNumerals(s) : s;
  };
  const numLoc = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  // Countdown to Iftar (live)
  const cd = useMemo(() => {
    const ms = Math.max(0, liveToday.iftar.getTime() - now.getTime());
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return { ms, h, m, s };
  }, [liveToday.iftar, now]);

  const fastingHrs = Math.floor(today.fastingMs / 3_600_000);
  const fastingMin = Math.floor((today.fastingMs % 3_600_000) / 60_000);

  const nextRamadan = useMemo(() => (ramadanNow ? null : findRamadanStart(date)), [ramadanNow, date]);

  // Notification setup
  const enableNotify = async () => {
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    if (notifyTimerRef.current) window.clearTimeout(notifyTimerRef.current);
    const ms = liveToday.iftar.getTime() - Date.now();
    if (ms <= 0) return;
    notifyTimerRef.current = window.setTimeout(() => {
      try {
        new Notification(lang === "bn" ? "ইফতারের সময়" : "Iftar time", {
          body: lang === "bn" ? `${city.bn}: ${fmt(liveToday.iftar)}` : `${city.en}: ${fmt(liveToday.iftar)}`,
        });
      } catch { /* noop */ }
    }, ms);
    setNotifyOn(true);
  };
  useEffect(() => {
    return () => { if (notifyTimerRef.current) window.clearTimeout(notifyTimerRef.current); };
  }, []);

  const ramadanDays = useMemo(
    () => ramadanMonthDays(city.lat, city.lon, method, date, precaution),
    [city.lat, city.lon, method, date, precaution],
  );

  const isToday = useMemo(() => {
    const a = date, b = new Date();
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }, [date]);

  const dateStr = date.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <CalculatorLayout
      slug="sehri-iftar-time"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="Moon"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      <div className={lang === "bn" ? "font-bn space-y-6" : "space-y-6"}>
        {/* Controls */}
        <div className="grid gap-3 md:grid-cols-4">
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.city)}</span>
            <select value={cityKey} onChange={(e) => setCityKey(e.target.value)}
              className="w-full rounded-md border border-border bg-muted px-2 py-2 text-sm focus:border-accent focus:bg-background focus:outline-none focus:ring-1 focus:ring-accent/30">
              {BD_CITIES.map((c) => (
                <option key={c.key} value={c.key}>{lang === "bn" ? c.bn : c.en}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.method)}</span>
            <select value={method} onChange={(e) => setMethod(e.target.value as MethodId)}
              className="w-full rounded-md border border-border bg-muted px-2 py-2 text-sm focus:border-accent focus:bg-background focus:outline-none focus:ring-1 focus:ring-accent/30">
              {METHODS.map((m) => <option key={m.id} value={m.id}>{lang === "bn" ? m.bn : m.en}</option>)}
            </select>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.date)}</span>
            <input type="date" value={date.toISOString().slice(0, 10)}
              onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) setDate(d); }}
              className="w-full rounded-md border border-border bg-muted px-2 py-2 text-sm" />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.precaution)}</span>
            <input type="number" min={0} max={10} value={precaution}
              onChange={(e) => setPrecaution(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
              className="w-full rounded-md border border-border bg-muted px-2 py-2 text-sm" />
          </label>
        </div>

        {/* Hero card */}
        {ramadanNow ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 p-5 dark:border-emerald-700/50 dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-amber-950/20">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  {numLoc(hijri.day)} {lang === "bn" ? "রমাদান" : "Ramadan"} {numLoc(hijri.year)} · {lang === "bn" ? city.bn : city.en}
                </p>
                <p className="text-base font-semibold">{dateStr}</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <Stat icon={<Sunrise size={16} />} label={t(STR.sehri)} value={fmt(today.sehriEnd)} />
              <Stat icon={<Utensils size={16} />} label={t(STR.iftar)} value={fmt(today.iftar)} />
              <Stat icon={<Moon size={16} />} label={t(STR.fasting)}
                value={`${numLoc(fastingHrs)}${lang === "bn" ? " ঘ " : "h "}${numLoc(fastingMin)}${lang === "bn" ? " মি" : "m"}`} />
            </div>

            {isToday && cd.ms > 0 && now < liveToday.iftar && (
              <div className="mt-5 rounded-xl bg-card/70 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t(STR.countdown)}</p>
                <p className="mt-1 font-mono text-4xl font-bold tabular-nums tracking-tight md:text-5xl">
                  {numLoc(String(cd.h).padStart(2, "0"))}:{numLoc(String(cd.m).padStart(2, "0"))}:{numLoc(String(cd.s).padStart(2, "0"))}
                </p>
                <Button size="sm" variant="outline" className="mt-3" onClick={enableNotify} disabled={notifyOn}>
                  {notifyOn ? <BellRing size={14} className="mr-1" /> : <Bell size={14} className="mr-1" />}
                  {notifyOn ? t(STR.notifyOn) : t(STR.notify)}
                </Button>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-border bg-card p-5">
            <p className="text-sm font-semibold">{t(STR.notRamadan)}</p>
            {nextRamadan && (
              <p className="mt-1 text-sm text-muted-foreground">
                {t(STR.nextRamadan)}: {nextRamadan.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
                <span className="ml-2 text-xs">({t(STR.approxNote)})</span>
              </p>
            )}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Stat icon={<Sunrise size={16} />} label={lang === "bn" ? "ফজর (আজ)" : "Fajr (today)"} value={fmt(today.sehriEnd)} />
              <Stat icon={<Utensils size={16} />} label={lang === "bn" ? "মাগরিব (আজ)" : "Maghrib (today)"} value={fmt(today.iftar)} />
            </div>
            <Link to="/prayer-times" className="mt-4 inline-block text-sm font-semibold text-accent hover:underline">
              {t(STR.prayerLink)}
            </Link>
          </motion.div>
        )}

        {/* Action row */}
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => setShowCal((s) => !s)}>{t(STR.showCalendar)}</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer size={14} className="mr-1" />{t(STR.print)}
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(new Date())}>
              {lang === "bn" ? "আজ" : "Today"}
            </Button>
          )}
          <span className="self-center text-xs text-muted-foreground">{hijriString(date, lang)} · {formatBanglaDate(gregorianToBangla(date), lang)}</span>
        </div>

        {/* Ramadan Calendar */}
        {showCal && (
          <section className="overflow-x-auto rounded-xl border border-border bg-card">
            <p className="border-b border-border p-3 text-sm font-semibold">
              {lang === "bn" ? "রমাদান ক্যালেন্ডার" : "Ramadan Calendar"} · {numLoc(getHijri(ramadanDays[0]?.date ?? date).year)} AH · {lang === "bn" ? city.bn : city.en}
            </p>
            <table className="w-full min-w-[560px] text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "bn" ? "রমাদান" : "Ramadan"}</th>
                  <th className="p-2 text-left">{lang === "bn" ? "তারিখ" : "Date"}</th>
                  <th className="p-2 text-left">{lang === "bn" ? "বার" : "Day"}</th>
                  <th className="p-2 text-left">{lang === "bn" ? "সেহরি" : "Sehri"}</th>
                  <th className="p-2 text-left">{lang === "bn" ? "ইফতার" : "Iftar"}</th>
                  <th className="p-2 text-left">{lang === "bn" ? "সময়" : "Length"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {ramadanDays.map((r, i) => {
                  const h = getHijri(r.date).day;
                  const fh = Math.floor(r.fastingMs / 3_600_000);
                  const fm = Math.floor((r.fastingMs % 3_600_000) / 60_000);
                  return (
                    <tr key={i}>
                      <td className="p-2 font-semibold">{numLoc(h)}</td>
                      <td className="p-2">{r.date.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", { day: "numeric", month: "short" })}</td>
                      <td className="p-2">{r.date.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", { weekday: "short" })}</td>
                      <td className="p-2 tabular-nums">{fmt(r.sehriEnd)}</td>
                      <td className="p-2 tabular-nums">{fmt(r.iftar)}</td>
                      <td className="p-2 tabular-nums">{numLoc(fh)}h {numLoc(fm)}m</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* Niyyat & Dua */}
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {lang === "bn" ? "রোজার নিয়ত" : "Niyyat (intention)"}
            </p>
            <p dir="rtl" lang="ar" className="mt-2 text-right font-arabic text-xl leading-relaxed">
              نَوَيْتُ أَنْ أَصُومَ غَدًا مِنْ شَهْرِ رَمَضَانَ الْمُبَارَكِ
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {lang === "bn"
                ? "নাওয়াইতু আন আছুমা গাদাম মিন শাহরি রমাদানাল মুবারক।"
                : "Nawaitu an asuma ghadan min shahri Ramadanal mubarak."}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {lang === "bn"
                ? "অর্থ: আমি আগামীকাল পবিত্র রমাদান মাসের রোজা রাখার নিয়ত করছি।"
                : "Meaning: I intend to fast tomorrow in the blessed month of Ramadan."}
            </p>
            <p className="mt-2 text-xs italic text-muted-foreground">
              {lang === "bn" ? "নিয়ত মূলত মনে মনে যথেষ্ট — মুখে উচ্চারণ আবশ্যক নয়।" : "Intention is in the heart — verbal recitation is not required."}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {lang === "bn" ? "ইফতারের দোয়া" : "Dua at Iftar"}
            </p>
            <p dir="rtl" lang="ar" className="mt-2 text-right font-arabic text-xl leading-relaxed">
              اَللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {lang === "bn"
                ? "আল্লাহুম্মা লাকা ছুম্‌তু ওয়া আলা রিযকিকা আফতারতু।"
                : "Allahumma laka sumtu wa ‘ala rizqika aftartu."}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {lang === "bn"
                ? "অর্থ: হে আল্লাহ! আপনার জন্য রোজা রেখেছি এবং আপনার রিজিক দিয়ে ইফতার করছি।"
                : "Meaning: O Allah, for You I fasted and with Your provision I break my fast."}
            </p>
          </div>
        </div>

        {/* Rules */}
        <article className="prose prose-sm dark:prose-invert max-w-none space-y-2 text-sm">
          <h2 className="text-lg font-bold">{lang === "bn" ? "রোজার বিধান (সংক্ষিপ্ত)" : "Fasting rules (brief)"}</h2>
          <h3 className="text-base font-semibold">{lang === "bn" ? "রোজা ভঙ্গের কারণ" : "Things that break fast"}</h3>
          <ul className="list-disc pl-5">
            <li>{lang === "bn" ? "ইচ্ছাকৃত খাওয়া বা পান করা" : "Intentional eating or drinking"}</li>
            <li>{lang === "bn" ? "ইচ্ছাকৃত বমি করা" : "Intentional vomiting"}</li>
            <li>{lang === "bn" ? "স্ত্রী-সঙ্গম" : "Intercourse during fasting hours"}</li>
            <li>{lang === "bn" ? "মাসিক বা প্রসব-পরবর্তী রক্তস্রাব" : "Menstruation or postpartum bleeding"}</li>
          </ul>
          <h3 className="text-base font-semibold">{lang === "bn" ? "যা রোজা ভাঙে না" : "Things that do NOT break fast"}</h3>
          <ul className="list-disc pl-5">
            <li>{lang === "bn" ? "ভুলে খাওয়া বা পান করা" : "Forgetfully eating or drinking"}</li>
            <li>{lang === "bn" ? "সতর্কভাবে দাঁত ব্রাশ (পেস্ট না গিলে)" : "Brushing teeth (without swallowing)"}</li>
            <li>{lang === "bn" ? "অনিচ্ছাকৃত বমি" : "Involuntary vomiting"}</li>
            <li>{lang === "bn" ? "চোখে ড্রপ" : "Eye drops"}</li>
          </ul>
          <h3 className="text-base font-semibold">{lang === "bn" ? "ইফতারের সুন্নত" : "Sunnahs of Iftar"}</h3>
          <p>{lang === "bn"
            ? "খেজুর বা পানি দিয়ে ইফতার শুরু করা, ইফতারের আগে দোয়া পড়া, এবং দ্রুত ইফতার করা সুন্নত।"
            : "Begin with dates or water, recite the dua, and hasten to break fast — these are Sunnahs."}
          </p>
          <h3 className="text-base font-semibold">{lang === "bn" ? "লাইলাতুল কদর" : "Laylatul Qadr"}</h3>
          <p>{lang === "bn"
            ? "রমাদানের শেষ দশ রাতের বিজোড় রাতগুলোতে অন্বেষণ করুন — হাজার মাসের চেয়ে উত্তম।"
            : "Seek it in the odd nights of the last 10 of Ramadan — better than a thousand months."}</p>
          <p className="text-xs text-muted-foreground">
            {lang === "bn"
              ? "চাঁদ দেখার ভিত্তিতে রমাদান শুরু/শেষ ১ দিন আগ-পিছ হতে পারে — বাংলাদেশে জাতীয় চাঁদ দেখা কমিটির ঘোষণা চূড়ান্ত।"
              : "Local moon sighting may shift Ramadan ±1 day — the Bangladesh National Moon Sighting Committee's announcement is final."}
          </p>
        </article>
      </div>
    </CalculatorLayout>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card/70 p-3">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
